import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit';
import { IApplicationState } from '.';
import ContentConstants from '../contentConstants';
import { EditState, ISurveyEditModel, ITranslationEditItem, IValidationModel } from '../models';
import { ISurveyTermEditModel } from '../models/ISurveyTermEditModel';
import { ISurveyTermItemAddModel } from '../models/ISurveyTermItemEditModel';
import * as SurveyAPI from '../services/SurveyAPI';
import * as SurveyDefinitionsAPI from '../services/SurveyDefinitionsAPI';
import { ActionTypes, ActionTypes as CommonActionTypes } from './ActionTypes';
import { ITranslationApiParameters, saveTranslations } from '../services/TranslationsAPI';
import Constants from '../constants';
import { ITranslationEditorInstance } from './TranslationEditorStore';
import _ from 'lodash';

export interface ISurveyDefinitionsState {
	isLoading: boolean;
	isError: boolean;
}

export const initialState: ISurveyDefinitionsState = {
	isLoading: false,
	isError: false,
};

export const loadSurveyTermsAdminData = createAsyncThunk(
	'surveyTermsAdmin/loadSurveyTermData',
	async (surveyId: number, { dispatch, rejectWithValue }) => {
		try {
			dispatch({ type: CommonActionTypes.SURVEY_TERM_ADMIN_LOADING });
			return await SurveyAPI.getSurvey(surveyId).then((surveyEditModel: IValidationModel<ISurveyEditModel>) => {
				dispatch({ type: ActionTypes.SURVEY_GET_SUCCEEDED, payload: surveyEditModel });
			});
		} catch (error) {
			dispatch({ type: ActionTypes.SURVEY_GET_FAILED });
			return rejectWithValue(error);
		}
	},
);

export const saveSurveyTermData = createAsyncThunk(
	'surveyTermEdit/saveSurveyTermData',
	async (surveyId: number, { dispatch, getState, rejectWithValue }) => {
		try {
			const state = getState() as IApplicationState;
			const surveyEditModel = state.survey.surveyEditModel;
			const surveyTermAdminEdit: ISurveyTermEditModel = {
				...surveyEditModel.surveyTerm,
				surveyTermItems: surveyEditModel.surveyTermItems,
			};
			const surveyTermEdit = await SurveyDefinitionsAPI.UpdateSurveyTerm(surveyId, surveyTermAdminEdit);

			const baseApi = (Constants.API_TRANSLATIONS_URL + '/{surveyId}/{propertyName}').replace(
				'{surveyId}',
				surveyEditModel.survey.surveyId.toString(),
			);

			surveyEditModel.surveyTermItems.forEach(async (def) => {
				saveSurveyDefinitionTranslations(
					baseApi,
					'term',
					def.surveyTermItemId,
					surveyEditModel.defaultCultureCode,
					state.translations.instances,
				);
				saveSurveyDefinitionTranslations(
					baseApi,
					'description',
					def.surveyTermItemId,
					surveyEditModel.defaultCultureCode,
					state.translations.instances,
				);
			});

			if (surveyEditModel.surveyTerm.surveyTermId) {
				saveSurveyDefinitionPageDescriptionTranslations(
					baseApi,
					surveyEditModel.surveyTerm.surveyTermId,
					surveyEditModel.defaultCultureCode,
					state.translations.instances,
				);
			}

			dispatch({ type: ActionTypes.SURVEY_TERM_ADMIN_SAVED, payload: surveyTermEdit });
		} catch (error) {
			return rejectWithValue(error);
		}
	},
);

async function saveSurveyDefinitionTranslations(
	baseApi: string,
	textColumnName: string,
	uniqueId: number,
	defaultCultureCode: string,
	translationInstances: { [key: string]: ITranslationEditorInstance },
) {
	const property = `survey-term-items/${uniqueId}/${textColumnName}`;
	const updatedTranslations = translationInstances[property].translations;

	// if no changes, don't call the API
	if (_.some(updatedTranslations, (i: ITranslationEditItem) => i.editState !== EditState.None)) {
		const apiParams: ITranslationApiParameters = {
			apiUrl: baseApi.replace('{propertyName}', property),
			uniqueId,
		};
		await saveTranslations(apiParams, defaultCultureCode, updatedTranslations);
	}
}

async function saveSurveyDefinitionPageDescriptionTranslations(
	baseApi: string,
	uniqueId: number,
	defaultCultureCode: string,
	translationInstances: { [key: string]: ITranslationEditorInstance },
) {
	const property = 'survey-term/page-description';
	const updatedTranslations = translationInstances[property].translations;

	// if no changes, don't call the API
	if (_.some(updatedTranslations, (i: ITranslationEditItem) => i.editState !== EditState.None)) {
		const apiParams: ITranslationApiParameters = {
			apiUrl: baseApi.replace('{propertyName}', property),
			uniqueId,
		};
		await saveTranslations(apiParams, defaultCultureCode, updatedTranslations);
	}
}

export const AddSurveyDefinition = createAsyncThunk(
	'SurveyDefinitions/AddSurveyDefinition',
	async (newSurveyTermItem: ISurveyTermItemAddModel, { dispatch, rejectWithValue }) => {
		try {
			const addedSurveyTermItem = await SurveyDefinitionsAPI.AddSurveyTermItem(newSurveyTermItem);
			dispatch({ type: CommonActionTypes.SURVEY_TERM_ITEM_ADD, payload: addedSurveyTermItem });
			return { addedSurveyTermItem };
		} catch (error) {
			dispatch({ type: CommonActionTypes.TOAST_WARNING_NOTIFICATION, payload: ContentConstants.TOAST_SURVEY_DEFINITION_ADD_FAILED });
			rejectWithValue(error);
		}
	},
);

const SurveyDefinitionsSlice = createSlice({
	name: 'SurveyDefinitions',
	initialState: initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder
			.addMatcher(isAnyOf(loadSurveyTermsAdminData.pending, saveSurveyTermData.pending), (state) => {
				state.isLoading = true;
				state.isError = false;
			})
			.addMatcher(isAnyOf(loadSurveyTermsAdminData.fulfilled, saveSurveyTermData.fulfilled), (state) => {
				state.isLoading = false;
			})
			.addMatcher(isAnyOf(loadSurveyTermsAdminData.rejected, saveSurveyTermData.rejected), (state) => {
				state.isLoading = false;
				state.isError = true;
			});
	},
});

export default SurveyDefinitionsSlice.reducer;
